import { NextRequest, NextResponse } from 'next/server';
import { createClient } from '@insforge/sdk';
import {
  COOKIE_NAMES,
  COOKIE_DEFAULTS,
  getBaseCookieOptions,
  getClearCookieOptions,
  serializeUserCookie,
} from '../lib/cookies';
import { InsforgeUser } from '@insforge/shared';
import { GetCurrentSessionResponse } from '@insforge/shared-schemas';

interface SignInRequestBody {
  action: 'sign-in';
  email: string;
  password: string;
}

interface SignUpRequestBody {
  action: 'sign-up';
  email: string;
  password: string;
}

interface SyncTokenRequestBody {
  action: 'sync-token';
}

type AuthRequestBody = SignInRequestBody | SignUpRequestBody | SyncTokenRequestBody;

export interface AuthRouteConfig {
  /**
   * Base URL of your Insforge backend
   */
  baseUrl: string;

  /**
   * Cookie max age in seconds (default: 7 days)
   * @default 604800
   */
  cookieMaxAge?: number;

  /**
   * Whether to use secure cookies (HTTPS only)
   * Auto-detected based on environment
   */
  secure?: boolean;
}

interface AuthRouteHandlers {
  POST: (request: NextRequest) => Promise<NextResponse>;
  GET: (request: NextRequest) => Promise<NextResponse>;
  DELETE: (request: NextRequest) => Promise<NextResponse>;
}

/**
 * Creates authentication route handlers for Next.js App Router
 * These handlers set HTTP-only cookies that can be read by middleware
 *
 * @example
 * ```ts
 * // app/api/auth/[...auth]/route.ts
 * import { createAuthRouteHandlers } from '@insforge/nextjs/api';
 *
 * const handlers = createAuthRouteHandlers({
 *   baseUrl: process.env.NEXT_PUBLIC_INSFORGE_BASE_URL!,
 * });
 *
 * export const POST = handlers.POST;
 * export const GET = handlers.GET;
 * export const DELETE = handlers.DELETE;
 * ```
 */
export function createAuthRouteHandlers(config: AuthRouteConfig): AuthRouteHandlers {
  const { baseUrl, cookieMaxAge = COOKIE_DEFAULTS.MAX_AGE } = config;

  const insforge = createClient({ baseUrl });

  /**
   * Helper to set auth cookies (session token + user identity)
   */
  function setAuthCookies(response: NextResponse, token: string, user: InsforgeUser) {
    const cookieOptions = getBaseCookieOptions({
      maxAge: cookieMaxAge,
      secure: config.secure,
    });

    // Set session cookie (access token)
    response.cookies.set({
      name: COOKIE_NAMES.SESSION,
      value: token,
      ...cookieOptions,
    });

    // Set user identity cookie (basic info for SSR)
    response.cookies.set({
      name: COOKIE_NAMES.USER,
      value: serializeUserCookie(user),
      ...cookieOptions,
    });

    return response;
  }

  /**
   * Helper to clear auth cookies
   */
  function clearAuthCookies(response: NextResponse) {
    const clearOptions = getClearCookieOptions({ secure: config.secure });

    // Clear session cookie
    response.cookies.set({
      name: COOKIE_NAMES.SESSION,
      value: '',
      ...clearOptions,
    });

    // Clear user cookie
    response.cookies.set({
      name: COOKIE_NAMES.USER,
      value: '',
      ...clearOptions,
    });

    return response;
  }

  /**
   * POST handler for authentication actions
   * Supports: sign-in, sign-up
   */
  async function POST(request: NextRequest) {
    try {
      const body = (await request.json()) as AuthRequestBody;
      const { action } = body;

      if (!action) {
        return NextResponse.json({ error: 'Action is required' }, { status: 400 });
      }

      switch (action) {
        case 'sign-in': {
          const { email, password } = body;
          if (!email || !password) {
            return NextResponse.json({ error: 'Email and password are required' }, { status: 400 });
          }

          const result = await insforge.auth.signInWithPassword({
            email,
            password,
          });

          if (result.error) {
            return NextResponse.json({ error: result.error.message }, { status: 401 });
          }

          if (!result.data || !result.data.user) {
            return NextResponse.json({ error: 'Authentication failed' }, { status: 401 });
          }

          const response = NextResponse.json({
            user: result.data.user,
            session: {
              userId: result.data.user.id,
              expiresAt: '',
              createdAt: new Date().toISOString(),
            },
          });

          setAuthCookies(response, result.data.accessToken || '', {
            id: result.data.user.id,
            email: result.data.user.email,
            profile: result.data.user.profile || null,
          });

          return response;
        }

        case 'sign-up': {
          const { email, password } = body;
          if (!email || !password) {
            return NextResponse.json({ error: 'Email and password are required' }, { status: 400 });
          }

          const result = await insforge.auth.signUp({ email, password });

          if (result.error) {
            return NextResponse.json({ error: result.error.message }, { status: 400 });
          }

          if (!result.data || !result.data.user) {
            return NextResponse.json({ error: 'Sign up failed' }, { status: 400 });
          }

          const response = NextResponse.json({
            user: result.data.user,
            session: {
              userId: result.data.user.id,
              expiresAt: '',
              createdAt: new Date().toISOString(),
            },
          });

          setAuthCookies(response, result.data.accessToken || '', {
            id: result.data.user.id,
            email: result.data.user.email,
            profile: result.data.user.profile || null,
          });

          return response;
        }

        case 'sync-token': {
          // Syncs access token to HTTP-only cookie for server-side usage
          // Token is passed via Authorization header (preferred)
          const authHeader = request.headers.get('Authorization');
          const tokenFromHeader = authHeader?.startsWith('Bearer ') ? authHeader.slice(7) : null;
          const token = tokenFromHeader;

          if (!token) {
            console.error('[Auth Route] No token provided in header');
            return NextResponse.json({ error: 'Token is required' }, { status: 400 });
          }

          // User data can be passed from client (preferred) or fetched from backend
          const { user: userFromBody } = body as { user?: InsforgeUser };

          // If user data is provided by client, use it directly (faster)
          if (userFromBody?.id && userFromBody?.email) {
            const nextResponse = NextResponse.json({
              user: userFromBody,
              session: {
                userId: userFromBody.id,
                expiresAt: '',
                createdAt: new Date().toISOString(),
              },
            });

            setAuthCookies(nextResponse, token, {
              id: userFromBody.id,
              email: userFromBody.email,
              profile: userFromBody.profile || null,
            });

            return nextResponse;
          }

          // Fallback: Verify the token and get user data from backend
          try {
            const response = await fetch(`${baseUrl}/api/auth/sessions/current`, {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });

            if (!response.ok) {
              const errorText = await response.text();
              console.error('[Auth API] Token validation failed:', errorText);
              return NextResponse.json(
                { error: 'Invalid token', details: errorText },
                { status: 401 }
              );
            }

            const session = (await response.json()) as GetCurrentSessionResponse;

            const nextResponse = NextResponse.json({
              user: session.user,
              session: {
                userId: session.user.id,
                expiresAt: '',
                createdAt: new Date().toISOString(),
              },
            });

            setAuthCookies(nextResponse, token, {
              id: session.user.id,
              email: session.user.email,
              profile: session.user.profile || null,
            });

            return nextResponse;
          } catch (error) {
            console.error('[Auth API] Token validation error:', error);
            return NextResponse.json(
              { error: 'Token validation failed', details: String(error) },
              { status: 401 }
            );
          }
        }

        default:
          return NextResponse.json({ error: 'Invalid action' }, { status: 400 });
      }
    } catch (error) {
      console.error('[Auth API Error]:', error);
      return NextResponse.json({ error: 'Internal server error' }, { status: 500 });
    }
  }

  /**
   * GET handler to check current session
   */
  async function GET(request: NextRequest) {
    try {
      const token = request.cookies.get(COOKIE_NAMES.SESSION)?.value;

      if (!token) {
        return NextResponse.json({ user: null, session: null });
      }

      // Verify token with backend
      const response = await fetch(`${baseUrl}/api/auth/sessions/current`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const nextResponse = NextResponse.json({ user: null, session: null });
        clearAuthCookies(nextResponse);
        return nextResponse;
      }

      const session = (await response.json()) as GetCurrentSessionResponse;

      return NextResponse.json({
        user: session.user,
      });
    } catch (error) {
      console.error('[Auth API Error]:', error);
      return NextResponse.json({ user: null, session: null });
    }
  }

  /**
   * DELETE handler for sign out
   */
  async function DELETE(request: NextRequest) {
    try {
      const token = request.cookies.get(COOKIE_NAMES.SESSION)?.value;

      if (token) {
        // Call backend sign out
        try {
          await fetch(`${baseUrl}/auth/sessions/current`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (error) {
          // Ignore backend errors during sign out
          console.error('[Auth API] Sign out error:', error);
        }
      }

      const response = NextResponse.json({ success: true });
      clearAuthCookies(response);

      return response;
    } catch (error) {
      console.error('[Auth API Error]:', error);
      return NextResponse.json({ error: 'Internal server error' }, { status: 500 });
    }
  }

  return {
    POST,
    GET,
    DELETE,
  };
}
